import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send session cookie on every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const message = error.response?.data?.error || error.message || 'An error occurred';

    // Strategy-related 404/410: emit event so UI can clear stale references
    if ((status === 404 || status === 410) && /\/api\/(strategies|trades|positions|planned-orders|orders)\//.test(url)) {
      window.dispatchEvent(new CustomEvent('strategy:gone', {
        detail: { url, status, message },
      }));
    }

    const err = new Error(message);
    err.status = status;
    return Promise.reject(err);
  }
);

// Strategies API
export const strategiesAPI = {
  getAll: (filters = {}) => api.get('/api/strategies', { params: filters }),
  getById: (id) => api.get(`/api/strategies/${id}`),
  create: (data) => api.post('/api/strategies', data),
  update: (id, data) => api.put(`/api/strategies/${id}`, data),
  delete: (id) => api.delete(`/api/strategies/${id}`),
  archive: (id, { liquidate = false } = {}) => api.delete(`/api/strategies/${id}`, { data: { liquidate } }),
  start: (id) => api.put(`/api/strategies/${id}/start`),
  stop: (id) => api.put(`/api/strategies/${id}/stop`),
  pause: (id) => api.put(`/api/strategies/${id}/pause`),
  resume: (id) => api.put(`/api/strategies/${id}/resume`),
  changeMode: (id, mode) => api.put(`/api/strategies/${id}/mode`, { mode }),
  liquidate: (id) => api.put(`/api/strategies/${id}/liquidate`),
  forceClosePositions: (id) => api.put(`/api/strategies/${id}/force-close-positions`),
  emergencyStopAll: () => api.post('/api/strategies/emergency-stop'),
  unarchive: (id) => api.put(`/api/strategies/${id}/unarchive`),
  permanentDelete: (id) => api.delete(`/api/strategies/${id}/permanent`),
};

// Trades API
export const tradesAPI = {
  getAll: (filters = {}) => api.get('/api/trades', { params: filters }),
  getById: (id) => api.get(`/api/trades/${id}`),
  getByStrategy: (strategyId, limit = 100) =>
    api.get(`/api/trades/strategy/${strategyId}`, { params: { limit } }),
  getStats: (strategyId) => api.get(`/api/trades/strategy/${strategyId}/stats`),
};

// Positions API
export const positionsAPI = {
  getAll: (filters = {}) => api.get('/api/positions', { params: filters }),
  getById: (id) => api.get(`/api/positions/${id}`),
  getByStrategy: (strategyId) => api.get(`/api/positions/strategy/${strategyId}`),
  getExposure: (strategyId) => api.get(`/api/positions/strategy/${strategyId}/exposure`),
  getPortfolio: () => api.get('/api/positions/portfolio'),
};

// Health API
export const healthAPI = {
  basic: () => api.get('/health'),
  detailed: () => api.get('/health/detailed'),
};

// Account API
export const accountAPI = {
  getInfo: (provider = 'alpaca', connectionId) => api.get(`/api/account/${provider}`, { params: connectionId ? { connectionId } : {} }),
  getSummary: () => api.get('/api/account/summary'),
  getLicense: () => api.get('/api/account/license'),
};

// Orders API
export const ordersAPI = {
  getByStrategy: (strategyId, status = 'open') =>
    api.get(`/api/orders/strategy/${strategyId}`, { params: { status } }),
  getAll: (params = {}) => api.get('/api/orders', { params }),
  getHistory: (params = {}) => api.get('/api/orders/history', { params }),
  cancel: (orderId) => api.delete(`/api/orders/${orderId}`),
};

// Planned Orders API
export const plannedOrdersAPI = {
  getByStrategy: (strategyId, params = {}) =>
    api.get(`/api/planned-orders/strategy/${strategyId}`, { params }),
  getById: (id) => api.get(`/api/planned-orders/${id}`),
  create: (strategyId, data) => api.post(`/api/planned-orders/strategy/${strategyId}`, data),
  cancel: (id) => api.delete(`/api/planned-orders/${id}`),
  preview: (strategyId, params = {}) =>
    api.post(`/api/planned-orders/strategy/${strategyId}/preview`, params),
};

// Assets API
export const assetsAPI = {
  search: (q, limit = 20, assetClass) =>
    api.get('/api/assets/search', { params: { q, limit, ...(assetClass ? { assetClass } : {}) } }),
  bars: (symbol, timeframe = '5m', limit = 200) =>
    api.get('/api/assets/bars', { params: { symbol, timeframe, limit } }),
  orderbook: (symbol) =>
    api.get('/api/assets/orderbook', { params: { symbol } }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params = {}) => api.get('/api/notifications', { params }),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put('/api/notifications/read-all'),
  deleteOne: (id) => api.delete(`/api/notifications/${id}`),
  deleteOld: (days = 14) => api.delete('/api/notifications/old', { params: { days } }),
  getPreferences: () => api.get('/api/notifications/preferences'),
  updatePreferences: (preferences) => api.put('/api/notifications/preferences', { preferences }),
};

// Broker Connections API
export const brokerAPI = {
  getAll:       ()           => api.get('/api/broker-connections'),
  create:       (data)       => api.post('/api/broker-connections', data),
  update:       (id, data)   => api.patch(`/api/broker-connections/${id}`, data),
  toggleStatus: (id)         => api.patch(`/api/broker-connections/${id}/status`),
  remove:       (id)         => api.delete(`/api/broker-connections/${id}`),
  test:         (data)       => api.post('/api/broker-connections/test', data),
  validate:     (data)       => api.post('/api/broker-connections/validate', data),
  getSchwabAuthUrl: (connectionId) => api.get(`/api/broker-connections/schwab/authorize?connectionId=${connectionId}`),
};

// Market Data Providers API
export const dataProviderAPI = {
  getAll:       ()           => api.get('/api/market-data-providers'),
  create:       (data)       => api.post('/api/market-data-providers', data),
  update:       (id, data)   => api.patch(`/api/market-data-providers/${id}`, data),
  toggleStatus: (id)         => api.patch(`/api/market-data-providers/${id}/status`),
  setRole:      (id, role)   => api.patch(`/api/market-data-providers/${id}/role`, { role }),
  remove:       (id)         => api.delete(`/api/market-data-providers/${id}`),
  test:         (data)       => api.post('/api/market-data-providers/test', data),
};

// Backtest API
export const backtestAPI = {
  run: (data) => api.post('/api/backtest', data),
};

// Push Notifications API
export const pushAPI = {
  getVapidKey: () => api.get('/api/push/vapid-key'),
  subscribe: (subscription) => api.post('/api/push/subscribe', subscription),
  unsubscribe: (data) => api.delete('/api/push/unsubscribe', { data }),
};

export default api;
